import { Alert } from '@cbm/shared';
import { AlertBadges } from './AlertStatusBadge';
import { AlertActions } from './AlertActions';
import { Clock, FileText, ExternalLink } from 'lucide-react';

interface AlertCardProps {
  alert: Alert;
  showActions?: boolean;
  showDetails?: boolean;
  onAcknowledge?: (alertId: string) => void;
  onClose?: (alertId: string) => void;
  onViewDetails?: (alertId: string) => void;
  onViewEvidence?: (alertId: string) => void;
  className?: string;
}

export function AlertCard({
  alert,
  showActions = true,
  showDetails = false,
  onAcknowledge,
  onClose,
  onViewDetails,
  onViewEvidence,
  className = ''
}: AlertCardProps) {
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const alertTime = new Date(timestamp);
    const diffMs = now.getTime() - alertTime.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'Just now';
  };

  return (
    <div className={`bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow ${className}`}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-sm font-medium text-gray-900">{alert.type}</h3>
              <span className="text-xs text-gray-500">#{alert.alertId.slice(-8)}</span>
            </div>
            <AlertBadges 
              severity={alert.severity}
              status={alert.status}
              owner={alert.owner}
            />
          </div>
          
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <Clock className="w-3 h-3" />
            <span>{getTimeAgo(alert.createdAt)}</span>
          </div>
        </div>

        {/* Summary */}
        <p className="text-sm text-gray-700 mb-3">{alert.summary}</p>

        {/* Details */}
        {showDetails && alert.description && (
          <div className="mb-3 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">{alert.description}</p>
          </div>
        )}

        {/* Machine and timestamps */}
        <div className="grid grid-cols-2 gap-4 text-xs text-gray-500 mb-3">
          <div>
            <span className="font-medium">Machine:</span> {alert.machineId}
          </div>
          <div>
            <span className="font-medium">Created:</span> {formatTimestamp(alert.createdAt)}
          </div>
          {alert.acknowledgedAt && (
            <>
              <div>
                <span className="font-medium">Acknowledged:</span> {formatTimestamp(alert.acknowledgedAt)}
              </div>
              <div>
                <span className="font-medium">By:</span> {alert.acknowledgedBy}
              </div>
            </>
          )}
          {alert.closedAt && (
            <>
              <div>
                <span className="font-medium">Closed:</span> {formatTimestamp(alert.closedAt)}
              </div>
              <div>
                <span className="font-medium">By:</span> {alert.closedBy}
              </div>
            </>
          )}
        </div>

        {/* Evidence */}
        {alert.evidenceRefs.length > 0 && (
          <div className="mb-3">
            <div className="flex items-center space-x-2 text-xs text-gray-600 mb-1">
              <FileText className="w-3 h-3" />
              <span>Evidence ({alert.evidenceRefs.length} files)</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {alert.evidenceRefs.slice(0, 3).map((ref, index) => (
                <button
                  key={index}
                  onClick={() => onViewEvidence?.(alert.alertId)}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {ref.split('/').pop()?.slice(0, 20)}...
                </button>
              ))}
              {alert.evidenceRefs.length > 3 && (
                <span className="text-xs text-gray-500">
                  +{alert.evidenceRefs.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        {showActions && (
          <div className="flex items-center justify-between pt-3 border-t">
            <AlertActions
              alert={alert}
              onAcknowledge={onAcknowledge}
              onClose={onClose}
              size="sm"
            />
            
            {onViewDetails && (
              <button
                onClick={() => onViewDetails(alert.alertId)}
                className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <ExternalLink className="w-3 h-3" />
                <span>View Details</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Compact alert card for lists
interface CompactAlertCardProps {
  alert: Alert;
  onAcknowledge?: (alertId: string) => void;
  onClose?: (alertId: string) => void;
  onClick?: (alertId: string) => void;
  className?: string;
}

export function CompactAlertCard({
  alert,
  onAcknowledge,
  onClose,
  onClick,
  className = ''
}: CompactAlertCardProps) {
  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const alertTime = new Date(timestamp);
    const diffMs = now.getTime() - alertTime.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);

    if (diffHours > 0) return `${diffHours}h`;
    if (diffMins > 0) return `${diffMins}m`;
    return 'now';
  };

  return (
    <div 
      className={`
        bg-white border rounded-lg p-3 hover:shadow-sm transition-shadow
        ${onClick ? 'cursor-pointer' : ''} ${className}
      `}
      onClick={() => onClick?.(alert.alertId)}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-sm font-medium text-gray-900 truncate">
              {alert.type}
            </span>
            <AlertBadges 
              severity={alert.severity}
              status={alert.status}
              owner={alert.owner}
            />
          </div>
          <p className="text-xs text-gray-600 truncate">{alert.summary}</p>
          <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
            <span>{alert.machineId}</span>
            <span>•</span>
            <span>{getTimeAgo(alert.createdAt)}</span>
          </div>
        </div>
        
        <div className="ml-3 flex-shrink-0">
          <AlertActions
            alert={alert}
            onAcknowledge={onAcknowledge}
            onClose={onClose}
            size="xs"
            showLabels={false}
          />
        </div>
      </div>
    </div>
  );
}